import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import PasteDropZone, { StagedFiles, withinSizeLimit } from '../../components/PasteDropZone';

const IMPACT_OPTIONS = [
    { value: 'cannot_continue', label: 'They cannot continue their work' },
    { value: 'major_delay', label: 'It is causing a major delay' },
    { value: 'workaround', label: 'They can continue using a workaround' },
    { value: 'minor', label: 'It is a minor issue or improvement' },
];

const EMPTY = {
    platform: '', request_type: 'issue', title: '', description: '', impact: 'workaround',
    submitted_by_name: '', submitted_by_email: '', submitted_by_org: '',
};

// Section 4.3 — deliberately minimal internal fallback for logging a request
// that came in via WhatsApp/phone/email instead of the Hub link. Tagged
// `origin=manual` server-side so Hub-adoption can be tracked on the dashboard.
export default function LogRequestDialog({ open, onClose, onCreated }) {
    const [platforms, setPlatforms] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState([]);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setForm(EMPTY);
        setFiles([]);
        api.get('/support/platforms').then(r => setPlatforms(r.data.items)).catch(() => {});
    }, [open]);

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const submit = async () => {
        if (!form.platform) return toast.error('Please select which platform this is for.');
        if (!form.title.trim() || !form.description.trim()) return toast.error('Please add a title and a short description.');
        if (!form.submitted_by_name.trim() || !form.submitted_by_email.trim()) return toast.error("Please add the client's name and email.");
        setSaving(true);
        try {
            const r = await api.post('/support/tickets', form);
            if (files.length) {
                try {
                    const fd = new FormData();
                    files.forEach(f => fd.append('files', f));
                    await api.post(`/support/tickets/${r.data.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                } catch {
                    toast.error(`Logged as ${r.data.ticket_number}, but the screenshots didn't upload. Add them from the ticket.`);
                }
            }
            toast.success(`Logged as ${r.data.ticket_number}`);
            onClose();
            onCreated && onCreated(r.data.id);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[520px]" data-testid="log-request-dialog">
                <DialogHeader>
                    <DialogTitle>Log a request on the client's behalf</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                        For the exceptions that still come in over WhatsApp, phone or email. Where possible, ask the client to use their Hub link instead.
                    </DialogDescription>
                </DialogHeader>
                <PasteDropZone onFiles={fs => setFiles(prev => [...prev, ...fs])} testId="log-request-dropzone">
                <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] text-[color:var(--bm-text-muted)]">Platform</label>
                            <Select value={form.platform} onValueChange={v => set('platform', v)}>
                                <SelectTrigger className="h-9 mt-1" data-testid="log-request-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
                                <SelectContent>
                                    {platforms.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[11px] text-[color:var(--bm-text-muted)]">Type</label>
                            <Select value={form.request_type} onValueChange={v => set('request_type', v)}>
                                <SelectTrigger className="h-9 mt-1" data-testid="log-request-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="issue">Issue</SelectItem>
                                    <SelectItem value="improvement">Improvement</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] text-[color:var(--bm-text-muted)]">Title</label>
                        <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Short summary" className="h-9 mt-1" data-testid="log-request-title" />
                    </div>
                    <div>
                        <label className="text-[11px] text-[color:var(--bm-text-muted)]">What did the client say?</label>
                        <Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className="mt-1" data-testid="log-request-description" />
                    </div>
                    <div>
                        <label className="text-[11px] text-[color:var(--bm-text-muted)]">Impact</label>
                        <Select value={form.impact} onValueChange={v => set('impact', v)}>
                            <SelectTrigger className="h-9 mt-1" data-testid="log-request-impact"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {IMPACT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] text-[color:var(--bm-text-muted)]">Client name</label>
                            <Input value={form.submitted_by_name} onChange={e => set('submitted_by_name', e.target.value)} className="h-9 mt-1" data-testid="log-request-client-name" />
                        </div>
                        <div>
                            <label className="text-[11px] text-[color:var(--bm-text-muted)]">Client email</label>
                            <Input value={form.submitted_by_email} onChange={e => set('submitted_by_email', e.target.value)} className="h-9 mt-1" data-testid="log-request-client-email" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] text-[color:var(--bm-text-muted)]">Company / organisation (optional)</label>
                        <Input value={form.submitted_by_org} onChange={e => set('submitted_by_org', e.target.value)} className="h-9 mt-1" data-testid="log-request-client-org" />
                    </div>
                    <div>
                        <label className="text-[11px] text-[color:var(--bm-text-muted)]">Screenshots (optional)</label>
                        <button type="button" onClick={() => fileRef.current?.click()}
                                className="mt-1 w-full rounded-md border border-dashed border-slate-300 px-3 py-3 text-[12px] text-[color:var(--bm-text-muted)] hover:bg-slate-50 flex items-center justify-center gap-1.5"
                                data-testid="log-request-attach-btn">
                            <ImagePlus size={14} /> Paste (Ctrl+V), drag in, or click to choose
                        </button>
                        <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden"
                               onChange={e => { const fs = withinSizeLimit(e.target.files); e.target.value = ''; setFiles(prev => [...prev, ...fs]); }} />
                        <StagedFiles files={files} onRemove={i => setFiles(prev => prev.filter((_, idx) => idx !== i))} testIdPrefix="log-request-staged-file" className="mt-2" />
                    </div>
                </div>
                </PasteDropZone>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: 'var(--bm-primary)' }} data-testid="log-request-submit-btn">
                        {saving ? 'Logging…' : 'Log request'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
